import { useState } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, Store, User, Phone, MapPin, CheckCircle, Building2 } from 'lucide-react';
import Button from '../components/Button';
import AddressPicker from '../components/AddressPicker';
import { useMerchant } from '../hooks/useMerchant';
import { supabase } from '../lib/supabase';
import { LAST_MERCHANT_EMAIL_KEY } from '../lib/persistSession';
import './Login.css';
import './MerchantLogin.css';

const MerchantLogin = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');

    const [email, setEmail] = useState(
        () => (typeof localStorage !== 'undefined' ? (localStorage.getItem(LAST_MERCHANT_EMAIL_KEY) ?? '').trim() : ''),
    );
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [registerForm, setRegisterForm] = useState({
        ownerName: '',
        email: '',
        phone: '',
        cnpj: '',
        establishmentName: '',
        address: '',
        latitude: 0,
        longitude: 0,
        category: '',
        description: '',
    });
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const { login, isAuthenticated, refreshMerchant } = useMerchant();

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const { error: authError } = await supabase.auth.signInWithPassword({
                email: email.trim(),
                password,
            });
            if (authError) throw new Error('Email ou senha incorretos');

            localStorage.setItem(LAST_MERCHANT_EMAIL_KEY, email.trim());
            const merchantRow = await refreshMerchant();

            if (!merchantRow?.establishmentId) {
                await supabase.auth.signOut();
                throw new Error('Conta sem estabelecimento vinculado. Contate o suporte.');
            }

            login(merchantRow);
            navigate('/merchant');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Email ou senha incorretos');
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });
    };

    const handleRegisterSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!registerForm.category) {
            setError('Por favor, selecione uma categoria para o seu estabelecimento.');
            return;
        }

        setLoading(true);

        try {
            const { error: insertError } = await supabase.from('MerchantRequest').insert(registerForm);
            if (insertError) throw new Error(insertError.message);
            setSubmitSuccess(true);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Erro ao enviar solicitação';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    if (isAuthenticated) {
        return <Navigate to="/merchant" replace />;
    }

    if (submitSuccess) {
        return (
            <div className="login-page">
                <div className="login-background">
                    <div className="bg-shape shape-1" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}></div>
                    <div className="bg-shape shape-2" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}></div>
                    <div className="bg-shape shape-3"></div>
                </div>

                <div className="login-container">
                    <div className="login-card success-card">
                        <div className="success-content">
                            <div className="success-icon">
                                <CheckCircle size={64} />
                            </div>
                            <h1>Solicitação Enviada!</h1>
                            <p>
                                Sua solicitação de parceria foi recebida com sucesso.
                                Nossa equipe irá analisar os dados e entrar em contato
                                em até <strong>3 dias úteis</strong>.
                            </p>
                            <div className="success-email">
                                <span>Confirmação enviada para:</span>
                                <strong>{registerForm.email}</strong>
                            </div>
                            <Button
                                variant="primary"
                                fullWidth
                                onClick={() => {
                                    setSubmitSuccess(false);
                                    setActiveTab('login');
                                    setRegisterForm({
                                        ownerName: '', email: '', phone: '', cnpj: '',
                                        establishmentName: '', address: '', latitude: 0, longitude: 0,
                                        category: '', description: '',
                                    });
                                }}
                                className="login-btn"
                                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                            >
                                Voltar ao Login
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page">
            <div className="login-background">
                <div className="bg-shape shape-1" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}></div>
                <div className="bg-shape shape-2" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}></div>
                <div className="bg-shape shape-3"></div>
            </div>

            <div className="login-container">
                <div className="login-card merchant-card">
                    <div className="login-header">
                        <div className="logo-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                            <Store size={32} />
                        </div>
                        <h1>Portal do Parceiro</h1>
                        <p>Gerencie seu estabelecimento e atraia mais clientes.</p>
                    </div>

                    <div className="merchant-tabs">
                        <button
                            className={`merchant-tab ${activeTab === 'login' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('login'); setError(''); }}
                        >
                            Já sou parceiro
                        </button>
                        <button
                            className={`merchant-tab ${activeTab === 'register' ? 'active' : ''}`}
                            onClick={() => { setActiveTab('register'); setError(''); }}
                        >
                            Quero ser parceiro
                        </button>
                    </div>

                    {activeTab === 'login' ? (
                        <form onSubmit={handleLogin} className="login-form">
                            {error && <div className="error-message">{error}</div>}

                            <div className="input-group">
                                <div className="input-icon"><Mail size={20} /></div>
                                <input type="email" placeholder="Email do lojista" value={email} onChange={(e) => setEmail(e.target.value.replace(/\s/g, ''))} required />
                            </div>

                            <div className="input-group">
                                <div className="input-icon"><Lock size={20} /></div>
                                <input type={showPassword ? 'text' : 'password'} placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} required />
                                <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>

                            <Button type="submit" variant="primary" fullWidth disabled={loading} className="login-btn"
                                style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                                {loading ? 'Entrando...' : 'Entrar no Portal'}
                            </Button>
                        </form>
                    ) : (
                        <form onSubmit={handleRegisterSubmit} className="login-form register-form">
                            {error && <div className="error-message">{error}</div>}

                            <div className="form-section-title"><User size={16} /><span>Dados do Responsável</span></div>

                            <div className="input-group">
                                <div className="input-icon"><User size={20} /></div>
                                <input type="text" name="ownerName" placeholder="Nome completo" value={registerForm.ownerName} onChange={handleRegisterChange} required />
                            </div>

                            <div className="input-row">
                                <div className="input-group">
                                    <div className="input-icon"><Mail size={20} /></div>
                                    <input type="email" name="email" placeholder="Email" value={registerForm.email} onChange={handleRegisterChange} required />
                                </div>
                                <div className="input-group">
                                    <div className="input-icon"><Phone size={20} /></div>
                                    <input type="tel" name="phone" placeholder="Telefone" value={registerForm.phone} onChange={handleRegisterChange} required />
                                </div>
                            </div>

                            <div className="input-group">
                                <div className="input-icon"><Building2 size={20} /></div>
                                <input type="text" name="cnpj" placeholder="CNPJ (00.000.000/0000-00)" value={registerForm.cnpj} onChange={handleRegisterChange} required />
                            </div>

                            <div className="form-section-title"><Store size={16} /><span>Dados do Estabelecimento</span></div>

                            <div className="input-group">
                                <div className="input-icon"><Store size={20} /></div>
                                <input type="text" name="establishmentName" placeholder="Nome do estabelecimento" value={registerForm.establishmentName} onChange={handleRegisterChange} required />
                            </div>

                            <div className="input-group" style={{ flexDirection: 'column', height: 'auto', gap: '0.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b', marginBottom: '0.5rem' }}>
                                    <MapPin size={20} /><span>Endereço e Localização</span>
                                </div>
                                <AddressPicker
                                    initialAddress={registerForm.address}
                                    onSelect={(address, lat, lng) => {
                                        setRegisterForm({ ...registerForm, address, latitude: lat, longitude: lng });
                                    }}
                                />
                                <input type="hidden" name="address" value={registerForm.address} required />
                            </div>

                            <div className="category-section">
                                <div className="category-label">Selecione a categoria:</div>
                                <div className="category-grid">
                                    {[
                                        { value: 'food', label: 'Restaurante / Alimentação' },
                                        { value: 'shop', label: 'Compras / Varejo' },
                                        { value: 'tour', label: 'Experiências / Turismo' },
                                        { value: 'gym', label: 'Serviços / Beleza' },
                                        { value: 'night', label: 'Entretenimento' },
                                    ].map((cat) => (
                                        <button key={cat.value} type="button"
                                            className={`category-btn ${registerForm.category === cat.value ? 'active' : ''}`}
                                            onClick={() => setRegisterForm({ ...registerForm, category: cat.value })}>
                                            {cat.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="input-group textarea-group">
                                <textarea name="description" placeholder="Descreva brevemente seu estabelecimento e os benefícios que deseja oferecer aos turistas..."
                                    value={registerForm.description} onChange={handleRegisterChange} rows={3} required />
                            </div>

                            <div className="terms-notice">
                                Ao enviar, você concorda com nossos <a href="#">Termos de Parceria</a> e <a href="#">Política de Privacidade</a>.
                            </div>

                            <Button type="submit" variant="primary" fullWidth disabled={loading} className="login-btn"
                                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
                                {loading ? 'Enviando...' : 'Enviar Solicitação'}
                            </Button>
                        </form>
                    )}

                    <div className="login-footer">
                        <p>É turista? <Link to="/login">Faça login aqui</Link></p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MerchantLogin;
